import {MonthlyTrendsKpisDto} from '@domain/kpis'
import {LegendItem} from '@hconnect/common/components/runningTimes/LegendItem'
import {customThemeConstants} from '@hconnect/uikit/src/lib2'
import {alpha, useTheme, Box, Stack} from '@mui/material'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Filler,
  Tooltip,
  Legend,
  ChartOptions
} from 'chart.js/auto'
import gradient from 'chartjs-plugin-gradient'
import moment from 'moment'
import {useMemo} from 'react'
import {Chart} from 'react-chartjs-2'
import {useTranslation} from 'react-i18next'
import useResizeObserver from 'use-resize-observer'

import {FEATURE_NAMESPACE} from '../consts'
import {getCurrentVsLastYearKpiChartDatasets} from '../mappers'

// Explicitly register Chart.js components to ensure they're available outside package boundaries
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Filler,
  Tooltip,
  Legend
)

const CHART_HEIGHT = 250
const chartPlugins = [gradient]
const DATASET_INDEX = {
  LAST_YEAR_ACTUAL: 0,
  ACTUAL: 1,
  PLANED: 2
} as const

const getMonthLabels = (language: string) =>
  Array.from({length: 12}, (_, i) => moment().month(i).locale(language).format('MMM'))

type Props = {
  data: MonthlyTrendsKpisDto
  currentYear: number
}

export const CurrentVsLastYearKpiChart = ({data, currentYear}: Props) => {
  const {t, i18n} = useTranslation(FEATURE_NAMESPACE)
  const language = i18n.language
  const {palette} = useTheme()
  const {ref: containerRef, width} = useResizeObserver()
  const lastYear = currentYear - 1
  const chartData = useMemo(() => {
    const {lastYearActual, currentYearPlanned, currentYearActual} =
      getCurrentVsLastYearKpiChartDatasets(data, currentYear)
    const yMax = Math.max(
      ...lastYearActual.filter((item) => item !== null),
      ...currentYearPlanned.filter((item) => item !== null),
      ...currentYearActual.filter((item) => item !== null)
    )
    const monthLabels = getMonthLabels(language)
    return {
      labels: monthLabels,
      datasets: [
        {
          index: DATASET_INDEX.LAST_YEAR_ACTUAL,
          label: t('currentVsLastYearKpiChart.label.actualAbbreviationWithYear', {
            year: lastYear
          }),
          data: lastYearActual,
          type: 'line' as const,
          fill: 'start' as const,
          borderColor: palette.primary.main,
          gradient: {
            backgroundColor: {
              axis: 'y' as const,
              colors: {
                [0]: alpha(palette.primary.main, 0),
                [yMax]: alpha(palette.primary.main, 0.2)
              }
            }
          },
          pointRadius: 0,
          borderWidth: 1.5
        },
        {
          index: DATASET_INDEX.ACTUAL,
          label: t('currentVsLastYearKpiChart.label.actualAbbreviation'),
          data: currentYearActual,
          type: 'bar' as const,
          backgroundColor: customThemeConstants().palette.accent2
        },
        {
          index: DATASET_INDEX.PLANED,
          label: t('currentVsLastYearKpiChart.label.plannedAbbreviation'),
          data: currentYearPlanned,
          type: 'bar' as const,
          backgroundColor: customThemeConstants().palette.accent1
        }
      ]
    }
  }, [data, currentYear, lastYear, language, t, palette])

  const chartOptions: ChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        gradient: {
          gradient
        },
        tooltip: {
          mode: 'index' as const,
          intersect: false,
          displayColors: false,
          padding: 8,
          callbacks: {
            title: () => '',
            label: (context) => {
              const month = context.label
              const year =
                context.datasetIndex !== DATASET_INDEX.LAST_YEAR_ACTUAL ? currentYear : lastYear
              const prefix =
                context.datasetIndex === DATASET_INDEX.PLANED
                  ? t('currentVsLastYearKpiChart.label.plannedAbbreviation')
                  : t('currentVsLastYearKpiChart.label.actualAbbreviation')
              return `${prefix} ${month} ${year}: ${context.formattedValue}`
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true
        }
      }
    }),
    [lastYear, currentYear, t]
  )

  return (
    <Stack spacing={2} data-test-id="current-vs-last-year-kpi-chart">
      <Box sx={{alignSelf: 'flex-end'}}>
        <CurrentVsLastYearKpiChartLegend lastYear={lastYear} />
      </Box>
      <Box ref={containerRef}>
        <Chart
          key={`current-vs-last-year-${CHART_HEIGHT}-${width}`}
          type="line"
          height={CHART_HEIGHT}
          width={width}
          data={chartData}
          options={chartOptions}
          plugins={chartPlugins}
        />
      </Box>
    </Stack>
  )
}

type CurrentVsLastYearKpiChartLegendProps = {
  lastYear: number
}

const CurrentVsLastYearKpiChartLegend = ({lastYear}: CurrentVsLastYearKpiChartLegendProps) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {palette} = useTheme()

  return (
    <Stack direction="row" spacing={1}>
      <LegendItem
        color={customThemeConstants().palette.accent2}
        label={t('currentVsLastYearKpiChart.label.actualAbbreviation')}
        variant="square"
      />
      <LegendItem
        color={customThemeConstants().palette.accent1}
        label={t('currentVsLastYearKpiChart.label.plannedAbbreviation')}
        variant="square"
      />
      <LegendItem
        color={palette.primary.main}
        label={t('currentVsLastYearKpiChart.label.actualAbbreviationWithYear', {
          year: lastYear
        })}
        variant="line"
      />
    </Stack>
  )
}
